import { createContext, useContext, useEffect, ReactNode, useState } from 'react'
import { User } from './types'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (benutzername: string, passwort: string) => Promise<{ success: boolean; error?: string }>
  logout: () => void
  refreshAuth: () => Promise<boolean>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  const getAuthStorageKey = () => {
    return (window as any).PILOT_HUB_CONFIG?.authStorageKey || 'pilot-hub-auth'
  }

  const [isRefreshing, setIsRefreshing] = useState(false)

  const checkAuth = async () => {
      // Prevent re-entry during token refresh
      if (isRefreshing) return
      
      setIsLoading(true)
      try {
        const authData = localStorage.getItem(getAuthStorageKey())
        if (!authData) {
          setIsLoading(false)
          return
        }

        const parsed = JSON.parse(authData)
        const token = parsed.accessToken || parsed.access_token || parsed.state?.accessToken || parsed.state?.access_token
        const refresh = parsed.refreshToken || parsed.refresh_token || parsed.state?.refreshToken || parsed.state?.refresh_token

        if (!token) {
          setIsLoading(false)
          return
        }

        const apiBase = (window as any).PILOT_HUB_CONFIG?.apiBaseUrl || '/api/v1'
        // Ohne Verbindung (Netzwerkfehler, 502-504) nicht abmelden: zuletzt angemeldeten Benutzer weiterverwenden,
        // damit Aktionen im Feld (Offline-Warteschlange) nach einem Reload nicht verloren gehen
        const zwischengespeicherterNutzer = parsed.user || parsed.state?.user || null
        let response: Response | null = null
        try {
          response = await fetch(`${apiBase}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        } catch {
          response = null
        }
        if (!response || response.status === 502 || response.status === 503 || response.status === 504) {
          if (zwischengespeicherterNutzer) {
            setUser(zwischengespeicherterNutzer)
            setAccessToken(token)
            setIsAuthenticated(true)
            return
          }
          throw new Error('Keine Verbindung')
        }

        if (response.ok) {
          const userData = await response.json()
          setUser(userData)
          setAccessToken(token)
          setIsAuthenticated(true)
        } else if (response.status === 401 && refresh) {
          // Try to refresh token
          setIsRefreshing(true)
          const refreshResponse = await fetch(`${apiBase}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refresh })
          })
          if (refreshResponse.ok) {
            const data = await refreshResponse.json()
            const newToken = data.access_token
            const newRefresh = data.refresh_token || refresh
            // Update localStorage
            const updatedAuth = {
              ...parsed,
              accessToken: newToken,
              access_token: newToken,
              refreshToken: newRefresh,
              refresh_token: newRefresh,
              state: {
                ...parsed.state,
                accessToken: newToken,
                access_token: newToken,
                refreshToken: newRefresh,
                refresh_token: newRefresh,
              }
            }
            localStorage.setItem(getAuthStorageKey(), JSON.stringify(updatedAuth))
            // Retry with new token
            response = await fetch(`${apiBase}/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            })
            if (response.ok) {
              const userData = await response.json()
              setUser(userData)
              setAccessToken(newToken)
              setIsAuthenticated(true)
            } else {
              throw new Error('Token invalid after refresh')
            }
          } else {
            throw new Error('Refresh failed')
          }
        } else {
          throw new Error('Token invalid')
        }
      } catch {
        localStorage.removeItem(getAuthStorageKey())
        setUser(null)
        setAccessToken(null)
        setRefreshToken(null)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }

  useEffect(() => {
    checkAuth()

    // Listen for auth changes from LoginPage (which writes directly to localStorage)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === getAuthStorageKey() && e.newValue !== e.oldValue) {
        checkAuth()
      }
    }

    // Also listen for same-tab changes (dispatchEvent workaround)
    const handleCustomAuthChange = () => checkAuth()
    window.addEventListener('pilot-hub-auth-changed', handleCustomAuthChange)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('pilot-hub-auth-changed', handleCustomAuthChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  const login = async (benutzername: string, passwort: string) => {
    setIsLoading(true)
    try {
      const config = (window as any).PILOT_HUB_CONFIG || {}
      const apiBase = config.apiBaseUrl || '/api/v1'
      const loginEndpoint = config.loginEndpoint || '/auth/login'
      const response = await fetch(`${apiBase}${loginEndpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ benutzername, password: passwort, rememberMe: true })
      })

      const data = await response.json()

      if (!response.ok) {
        return { success: false, error: data.detail || 'Anmeldung fehlgeschlagen' }
      }

      const { access_token, refresh_token, user } = data

      setAccessToken(access_token)
      setRefreshToken(refresh_token)
      setUser(user)
      setIsAuthenticated(true)

      const authData = {
        accessToken: access_token,
        refreshToken: refresh_token,
        user,
        state: {
          accessToken: access_token,
          access_token: access_token,
          refreshToken: refresh_token,
          refresh_token: refresh_token,
          user: user
        }
      }
      localStorage.setItem(getAuthStorageKey(), JSON.stringify(authData))

      // NO dispatchEvent here - login() sets state directly, no need to trigger checkAuth()
      // Event is only for cross-tab sync (storage event)

      return { success: true }
    } catch {
      return { success: false, error: 'Ein unerwarteter Fehler ist aufgetreten' }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = () => {
    setUser(null)
    setAccessToken(null)
    setRefreshToken(null)
    setIsAuthenticated(false)
    localStorage.removeItem(getAuthStorageKey())
    window.location.href = '/login'
  }

  const refreshAuth = async () => {
    try {
      const authData = localStorage.getItem(getAuthStorageKey())
      if (!authData) return false

      const parsed = JSON.parse(authData)
      const refresh = parsed.refreshToken || parsed.refresh_token

      if (!refresh) return false

      const apiBase = (window as any).PILOT_HUB_CONFIG?.apiBaseUrl || '/api/v1'
      const response = await fetch(`${apiBase}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh })
      })

      if (response.ok) {
        const data = await response.json()
        const newAccessToken = data.access_token

        setAccessToken(newAccessToken)
        const updatedAuth = {
          ...parsed,
          accessToken: newAccessToken,
          refreshToken: data.refresh_token || refresh
        }
        localStorage.setItem(getAuthStorageKey(), JSON.stringify(updatedAuth))
        return true
      }
    } catch {
      console.error('Auth refresh failed')
    }
    return false
  }

  return (
    <AuthContext.Provider value={{
      user,
      accessToken,
      refreshToken,
      isLoading,
      isAuthenticated,
      login,
      logout,
      refreshAuth
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}